import {
  AfterContentChecked,
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  Input,
  OnInit,
  ViewChild,
} from '@angular/core';
import { HandleType } from './selection-area.component';
import { ImageBanner } from 'app/services/app-settings.types';
import { FileUploader } from 'app/settings/types';

@Component({
  selector: 'ad-banner-editor',
  templateUrl: 'ad-banner-editor.html',
  standalone: false
})
export class AdBannerEditorComponent implements OnInit, AfterViewInit, AfterContentChecked {
  @Input() definition: ImageBanner;
  @Input() uploader: FileUploader;
  @Input() descriptionKey: string;

  elementLeft: number;
  elementTop: number;
  elementWidth: number;
  elementHeight: number;
  mousePressed: boolean;
  mousePressedX: number;
  mousePressedY: number;
  newAreaCreated = false;
  originalWidth: number;
  scaleFactor = 1;
  clickMapStyles = [];
  mouseMoveObservable = new SimpleObservable();
  uploading = false;
  uploadProgress = 0;
  @ViewChild('bannerImage') image: ElementRef<HTMLImageElement>;
  handleForLastClickArea: HandleType;

  constructor(private changeDetector: ChangeDetectorRef) {}

  ngOnInit() {
    if (this.definition?.url) {
      this.updateClickAreas();
    }
    if (!this.definition.clickMap) {
      this.definition.clickMap = [];
    }
  }

  ngAfterViewInit() {
    this.updateModel();
  }

  ngAfterContentChecked() {
    if (this.image?.nativeElement.offsetParent != null) {
      this.updateModel();
    }
  }

  refreshContainerCoordinates() {
    if (!this.image) {
      return;
    }
    const rect = this.image.nativeElement.parentElement.getBoundingClientRect();
    this.elementLeft = rect.left;
    this.elementTop = rect.top;
    this.elementWidth = rect.width;
    this.elementHeight = rect.height;
  }

  updateClickAreas() {
    const tempImg = new Image();
    tempImg.onload = () => {
      if (!this.originalWidth) {
        this.originalWidth = tempImg.width;
      }
      this.updateModel();
      this.changeDetector.detectChanges();
    };
    tempImg.src = this.definition.url;
  }

  onClickAreaUpdated(areaIndex: number, clickAreaDef) {
    const rectDef = clickAreaDef.rectangleDefinition;
    this.scaleFactor = this.image.nativeElement.width / this.originalWidth;
    const clickMapDef = this.definition.clickMap[areaIndex];
    if (clickMapDef) {
      clickMapDef.url = clickAreaDef.url;
      const x1 = Math.floor(rectDef.left / this.scaleFactor);
      const y1 = Math.floor(rectDef.top / this.scaleFactor);
      const x2 = Math.floor((rectDef.left + rectDef.width) / this.scaleFactor);
      const y2 = Math.floor((rectDef.top + rectDef.height) / this.scaleFactor);
      clickMapDef.shapeCoords = `${x1},${y1},${x2},${y2}`;
    }
    this.translateCoordinates();
  }

  @HostListener('window:scroll', [])
  @HostListener('window:resize', [])
  onResize() {
    this.updateModel();
    this.changeDetector.detectChanges();
  }

  updateModel() {
    this.refreshContainerCoordinates();
    this.translateCoordinates();
  }

  translateCoordinates() {
    if (!this.originalWidth || !this.image) {
      return;
    }
    this.scaleFactor = this.image.nativeElement.width / this.originalWidth;
    this.clickMapStyles = this.definition.clickMap.map((clickAreaDef) => {
      const rectCoords = clickAreaDef.shapeCoords
        .split(',')
        .map((value) => parseInt(value.trim(), 10))
        .map((value) => value * this.scaleFactor);
      return {
        left: rectCoords[0],
        top: rectCoords[1],
        width: rectCoords[2] - rectCoords[0],
        height: rectCoords[3] - rectCoords[1],
      };
    });
  }

  onMouseMove(event: MouseEvent) {
    if (this.containerIsDefined() && this.mouseWithinContainer(event)) {
      if (this.mousePressed && !this.newAreaCreated) {
        this.newAreaCreated = true;
        const mouseX = Math.round((event.clientX - this.elementLeft) / this.scaleFactor);
        const mouseY = Math.round((event.clientY - this.elementTop) / this.scaleFactor);
        const shapeCoords = `${this.mousePressedX},${this.mousePressedY},${mouseX},${mouseY}`;
        this.definition.clickMap.push({ url: undefined, shapeType: 'rect', shapeCoords });
        this.handleForLastClickArea =
          mouseX > this.mousePressedX || mouseY > this.mousePressedY ? HandleType.BOTTOM : HandleType.TOP;
        this.translateCoordinates();
      } else {
        this.mouseMoveObservable.notify({ x: event.clientX - this.elementLeft, y: event.clientY - this.elementTop });
      }
    }
    event.preventDefault();
  }

  containerIsDefined(): boolean {
    return this.elementLeft !== undefined && this.elementTop !== undefined;
  }

  mouseWithinContainer(event: MouseEvent): boolean {
    return (
      event.clientX >= this.elementLeft &&
      event.clientX <= this.elementLeft + this.elementWidth &&
      event.clientY >= this.elementTop &&
      event.clientY <= this.elementTop + this.elementHeight
    );
  }

  deleteClickArea(index: number) {
    this.definition.clickMap.splice(index, 1);
    this.updateModel();
  }

  onMouseDown(event) {
    this.mousePressed = true;
    this.newAreaCreated = false;
    this.handleForLastClickArea = undefined;
    this.mousePressedX = Math.round((event.clientX - this.elementLeft) / this.scaleFactor);
    this.mousePressedY = Math.round((event.clientY - this.elementTop) / this.scaleFactor);
    event.preventDefault();
  }

  onMouseUp() {
    this.mousePressed = false;
    this.newAreaCreated = false;
    this.handleForLastClickArea = undefined;
  }

  uploadImage(event) {
    this.uploading = true;
    this.uploadProgress = 0;
    const file: File = event.target.files[0];
    if (file) {
      this.uploader(file, (progress) => this.onUploadProgress(progress))
        .then((url) => {
          return new Promise<string>((resolve, reject) => {
            const tempImg = new Image();
            tempImg.onload = () => resolve(url);
            tempImg.onerror = () => reject();
            tempImg.src = url;
          });
        })
        .then((url) => {
          this.definition.url = url;
          this.updateClickAreas();
        })
        .catch((e) => {
          // TODO show toast
          console.log('Upload failed', e);
        })
        .finally(() => {
          this.uploading = false;
          this.uploadProgress = undefined;
        });
    }
  }

  onUploadProgress(progress: number) {
    this.uploadProgress = progress;
  }

  removeImage() {
    this.definition.url = null;
    this.definition.clickMap = [];
  }
}

class SimpleObservable {
  private subscribers: { (any): any }[] = [];

  public subscribe(callback: { (any): any }) {
    this.subscribers.push(callback);
  }

  public notify(event) {
    this.subscribers.forEach((s) => s(event));
  }
}
